/**
 * LRU Cache for TTS audio data
 * Caches audio ArrayBuffers keyed by text content hash
 * Auto-evicts oldest entries when limit is exceeded
 */

const CACHE_LIMIT = 50;

interface CacheEntry {
  audio: ArrayBuffer;
  timestamp: number;
}

class TTSCache {
  private cache: Map<string, CacheEntry> = new Map();
  private readonly limit: number;

  constructor(limit: number = CACHE_LIMIT) {
    this.limit = limit;
  }

  /**
   * Generate a simple hash for the text content
   * Uses a fast string hashing algorithm
   */
  private hashText(text: string): string {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return `tts_${hash.toString(36)}`;
  }

  /**
   * Check if audio exists in cache for given text
   */
  has(text: string): boolean {
    const key = this.hashText(text);
    return this.cache.has(key);
  }

  /**
   * Get cached audio for given text
   * Updates timestamp for LRU tracking
   */
  get(text: string): ArrayBuffer | null {
    const key = this.hashText(text);
    const entry = this.cache.get(key);

    if (entry) {
      // Update timestamp for LRU
      entry.timestamp = Date.now();
      return entry.audio;
    }

    return null;
  }

  /**
   * Store audio in cache for given text
   * Evicts oldest entries if cache limit exceeded
   */
  set(text: string, audio: ArrayBuffer): void {
    const key = this.hashText(text);

    // Evict oldest entries if at limit
    if (this.cache.size >= this.limit && !this.cache.has(key)) {
      this.evictOldest();
    }

    this.cache.set(key, {
      audio,
      timestamp: Date.now()
    });
  }

  /**
   * Evict the oldest entry based on timestamp
   */
  private evictOldest(): void {
    let oldestKey: string | null = null;
    let oldestTime = Infinity;

    for (const [key, entry] of this.cache.entries()) {
      if (entry.timestamp < oldestTime) {
        oldestTime = entry.timestamp;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  /**
   * Clear all cached audio
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Get current cache size
   */
  get size(): number {
    return this.cache.size;
  }
}

// Singleton instance
const ttsCache = new TTSCache();

export default ttsCache;
export { TTSCache };
